import { useContext } from 'react';
import { ExerciseResult } from '../../@types/exercise-types';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import Dialog from '@pelckmans/elementis/components/dialog';
import Button from '@pelckmans/elementis/components/button';
import { ExerciseTranslationsContext } from '../../contexts/ExerciseTranslationsContext';
import ExerciseHighLightedItem from '../exercise-highlighted-item';

interface LaunchModalPausedSessionProps {
  onResume?: () => void;
  onRestart?: () => void;
  result: ExerciseResult;
}

function LaunchModalPausedSession({ onResume, onRestart, result }: LaunchModalPausedSessionProps): JSX.Element | null {
  const translations = useContext(ExerciseTranslationsContext);

  const { medialink, module, onClose } = useExerciseManagerContext();

  return (
    <Dialog
      id="launch-modal-paused-session"
      modal
      onClose={onClose}
      aria-label={translations.launchModalPausedSession.a11yLabel}
    >
      <Dialog.Header
        title={translations.launchModalPausedSession.title}
        onClose={onClose}
        aria-label={translations.closeDialog}
      />
      <Dialog.Body>
        <div className="modal-content-text">
          {onRestart ? (
            <p>
              {translations.launchModalPausedSession.message.p1.restartable}
              <br /> {translations.launchModalPausedSession.message.p2}
            </p>
          ) : (
            <p>{translations.launchModalPausedSession.message.p1.nonRestartable}</p>
          )}
        </div>
        <ExerciseHighLightedItem
          module={module}
          status="busy"
          medialink={medialink}
          title={translations.launchModalPausedSession.highlightedItem.title}
          scoreTitle={translations.launchModalPausedSession.highlightedItem.score}
          percentageTitle={translations.launchModalPausedSession.highlightedItem.percentage}
          result={{
            title: translations.launchModalPausedSession.highlightedItem.resultTitle,
            score: result.score,
          }}
        />
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          onRestart
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onRestart}>
                    {translations.launchModalPausedSession.buttons.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button onClick={onClose} prominence="neutral" key="cancel">
                {translations.launchModalPausedSession.buttons.cancel}
              </Button>
              {onResume && (
                <Button onClick={onResume} prominence="success" key="success">
                  {translations.launchModalPausedSession.buttons.resume}
                </Button>
              )}
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default LaunchModalPausedSession;
